import { useCallback } from 'react';
import { css } from '@emotion/react';
import { PrintDialog } from '@frontend/assets';
import { InfinitePaginatedList } from '@frontend/components';
import { formatCentsToCurrency } from '@frontend/currency';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { CardBrand } from '@frontend/payments-card-brand';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useAppScopeStore, useWeTags } from '@frontend/scope';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import {
  Avatar,
  Chip,
  ContentLoader,
  ListRow,
  NakedUl,
  PrintIcon,
  Table,
  Text,
  UpdateIcon,
} from '@frontend/design-system';
import {
  SearchFailedPaymentsModel,
  SearchFailedPaymentsOrder,
  SearchFailedPaymentsParams,
} from '../../../hooks/payment-failed/types';
import { mapFailedPaymentsTypeToPaymentType } from '../../../hooks/payment-failed/utils';
import { useCanDoActionsMulti } from '../../../hooks/use-can-do-actions-multi';
import { UseFailedTransactionsFiltersMethods } from '../../../hooks/use-failed-transactions-filters';
import {
  useQueryPaginatedFailedInvoices,
  useQueryAllFailedInvoices,
} from '../../../hooks/use-failed-transactions-queries';
import { useGenerateColumns } from '../../../hooks/use-generate-columns';
import { getPaymentMethodBrand, PaymentsTableInstances } from '../../../utils/helpers';
import { PrintHeader } from '../../PrintHeader';
import { SkeletonListLoader } from '../../Shared/skeleton-list-loader';
import { FailedTransactionsFilter } from '../FailedFilter/failed-transactions-filter';
import { generateColumns } from './generate-columns';
import { StatusChip } from './status-chip';
import { usePaymentFailedCsvExport } from './use-payment-failed-csv-export';

const FIELD_TO_ORDER: { [key: string]: SearchFailedPaymentsOrder } = {
  submittedAt: 'submittedAt',
  customerName: 'customerName',
};

export type FailedListProps = {
  handlePrint: () => void;
  filter: SearchFailedPaymentsParams;
  currentPage: number;
  filterMethods: UseFailedTransactionsFiltersMethods;
  onChangeFilter?: (filter: SearchFailedPaymentsParams) => void;
};

export const FailedList = ({
  filter,
  currentPage: initialPage,
  handlePrint,
  filterMethods: { setOrder, setNumRows, setCurrentPage, setFilter },
  onChangeFilter,
}: FailedListProps) => {
  const { t } = useTranslation('payments');
  const navigate = useNavigate();
  const { selectedLocationIds } = useAppScopeStore();
  const { canExport } = useCanDoActionsMulti(selectedLocationIds, 'any');

  const columns = useGenerateColumns<SearchFailedPaymentsModel>(generateColumns, false, PaymentsTableInstances.Failed, {
    hideLocationNameColumn: selectedLocationIds.length === 1,
  });
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });
  const {
    transactions,
    refetch,
    loading,
    hasMore,
    hasPrevious,
    fetchNextPage,
    fetchPreviousPage,
    currentPage,
    numRows,
  } = useQueryPaginatedFailedInvoices({
    currentPage: initialPage,
    onChangePage: setCurrentPage,
    searchParams: filter,
    onResetPage: () => setCurrentPage(1),
  });

  const { ExportAction, ExportFailedTransactionsModal } = usePaymentFailedCsvExport({
    filter,
    currentPage,
    canExport,
  });

  const handlePrintClick = () => {
    if (!transactions.length) {
      console.error('No transactions to print');
    } else {
      handlePrint();
    }
  };

  const handleApplyFilter = useCallback(
    (newFilter: SearchFailedPaymentsParams) => {
      setFilter({ ...filter, ...newFilter });
      onChangeFilter?.({ ...filter, ...newFilter });
      setCurrentPage(1);
    },
    [filter]
  );

  return (
    <>
      {isMobile ? (
        <MobileFailedList
          currentPage={initialPage}
          onChangePage={setCurrentPage}
          searchParams={filter}
          onResetPage={() => setCurrentPage(1)}
        />
      ) : (
        <Table
          tableStyle={css`
            min-height: 500px;
            height: 100%;
          `}
          globalTrackingId='nwx:failed-transactions'
          colConfig={columns}
          data={transactions}
          tableInstanceId={PaymentsTableInstances.Refunds}
          isLoading={loading}
          loadingRowsCount={numRows}
          manualSortBy
          hasResizeColumns
          hasResponsiveColWidths
          manualFilters
          manualFiltersRender={(modalProps, setShowNotification) => (
            <FailedTransactionsFilter
              data-trackingid='nwx:failed-transactions:filter'
              initialFilter={filter}
              onApply={handleApplyFilter}
              modalProps={modalProps}
              setShowNotification={setShowNotification}
            />
          )}
          emptyStateConfig={{
            type: 'payments',
            header: t('No data to display'),
          }}
          tableActions={[
            {
              label: t('Print'),
              Icon: PrintIcon,
              onClick: handlePrintClick,
            },
            ...(canExport ? [ExportAction] : []),
            {
              label: t('Refresh'),
              Icon: UpdateIcon,
              onClick: () => refetch(),
            },
          ]}
          styleConfig={{
            columns: [
              {
                id: '*',
                cellStyler: css`
                  padding: ${theme.spacing(1, 1.5)};
                `,
                headerStyler: css`
                  padding: ${theme.spacing(2, 1.5)};
                `,
              },
            ],
          }}
          onSortChange={(res) => {
            const { id, value } = res[0] || {};
            if (id) {
              const order = FIELD_TO_ORDER[id];
              setOrder(
                order
                  ? (`${value === 'asc' ? '' : '-'}${FIELD_TO_ORDER[id]}` as SearchFailedPaymentsOrder)
                  : '-submittedAt'
              );
            } else {
              setOrder('-submittedAt');
            }
          }}
          rowActions={{
            onRowClick: (row) => {
              if (row.invoiceId)
                navigate({
                  to: '/payments/invoices/$id',
                  params: { id: row.invoiceId },
                  search: { from: 'failed' },
                });
            },
          }}
          isPaginated
          manualPaginationConfig={{
            page: currentPage,
            hasNext: hasMore && !loading,
            hasPrevious: hasPrevious && currentPage !== 1 && !loading,
            onNumRowsChange: setNumRows,
            defaultRowsPerPage: numRows,
            rowsPerPageOptions: [10, 25, 50, 75, 100],
            handleChange: (prevOrNext: 'prev' | 'next') => {
              if (prevOrNext === 'prev') fetchPreviousPage();
              if (prevOrNext === 'next') fetchNextPage();
            },
          }}
          hasGlobalSearch={true}
          globalSearchConfig={{
            initialValue: filter.search.personName || '',
            position: 'right',
            searchHandler: (searchText: string) => {
              const toSet = {
                ...filter,
                search: {
                  ...filter.search,
                  personName: searchText.trim().length > 0 ? searchText : undefined,
                },
              };

              if (toSet.search.personName !== filter.search.personName) {
                setFilter(toSet);
                onChangeFilter?.(toSet);
              }
            },
            placeholder: t('Search'),
            debounceDelay: 1000,
          }}
        />
      )}

      {ExportFailedTransactionsModal}
    </>
  );
};

const MobileFailedList = ({
  searchParams,
  currentPage,
  onChangePage,
  onResetPage,
}: {
  searchParams: SearchFailedPaymentsParams;
  currentPage: number;
  onChangePage: (page: number) => void;
  onResetPage: () => void;
}) => {
  const { t } = useTranslation('payments');
  const navigate = useNavigate();
  const { accessibleLocationData } = useAppScopeStore();
  const getLocationName = (locationId: string) => accessibleLocationData[locationId ?? '']?.name ?? '';
  const showCompactListRow = useWeTags('compact-list-row');
  const { infiniteQueryProps } = useQueryPaginatedFailedInvoices({
    searchParams,
    currentPage,
    onChangePage,
    onResetPage,
    select: (data) => ({
      pageParams: data.pageParams,
      pages: data.pages.map((page) => ({
        ...page,
        rows: page.payments,
      })),
    }),
  });

  const RenderListItem = ({ listItem }: { listItem: SearchFailedPaymentsModel }) => {
    return (
      <ListRow
        as='div'
        css={{ gridTemplateAreas: '"lead content"' }}
        onClick={() => {
          if (listItem.invoiceId)
            navigate({
              to: '/payments/invoices/$id',
              params: { id: listItem.invoiceId },
              search: { from: 'failed' },
            });
        }}
      >
        <ListRow.Lead>
          <Avatar
            firstName={listItem.personFirstName}
            lastName={listItem.personLastName}
            size={showCompactListRow ? 'small' : 'medium'}
          />
        </ListRow.Lead>
        <ListRow.Content>
          <ListRow.Content.Title
            as='div'
            css={[
              {
                alignItems: 'flex-start',
                display: 'flex',
                gap: theme.spacing(1),
                justifyContent: 'space-between',
                paddingRight: 0,
              },
              showCompactListRow && {
                alignItems: 'center',
                fontWeight: theme.font.weight.bold,
                color: theme.colors.neutral90,
                fontSize: theme.fontSize(16),
              },
            ]}
            isCompactMode={showCompactListRow}
          >
            <span>
              {listItem.personFirstName} {listItem.personLastName}
            </span>
            {listItem.locationId && showCompactListRow && (
              <Chip.Location
                maxWidth=''
                tooltipLabel={getLocationName(listItem.locationId)}
                css={{ fontSize: theme.fontSize(12), width: 20, padding: theme.spacing(0.5), gap: 0, height: 16 }}
              ></Chip.Location>
            )}
            {!showCompactListRow && <StatusChip status={listItem.paymentStatus} />}
          </ListRow.Content.Title>
          {!showCompactListRow && (
            <ListRow.Content.Subtitle as='div'>
              {listItem.locationId && <Chip.Location maxWidth=''>{getLocationName(listItem.locationId)}</Chip.Location>}
            </ListRow.Content.Subtitle>
          )}
          <ListRow.Content.TertiaryTitle as='div'>
            <NakedUl
              css={css`
                display: flex;
                flex-direction: row;
                gap: ${theme.spacing(3)};
                li {
                  display: flex;
                  flex-direction: column;
                }
              `}
            >
              <li>
                <Text size='small' color='light'>
                  {t('Amount')}
                </Text>
                <div css={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Text size={showCompactListRow ? 'medium' : 'large'}>{formatCentsToCurrency(listItem.amount)}</Text>
                  {showCompactListRow && <StatusChip status={listItem.paymentStatus} size='tiny' />}
                </div>
              </li>
              <li>
                <Text size='small' color='light'>
                  {t('Payment Method')}
                </Text>
                <CardBrand
                  brand={getPaymentMethodBrand({
                    paymentType: mapFailedPaymentsTypeToPaymentType(listItem.paymentType),
                    brand: listItem.cardBrand,
                    bankName: listItem.bankName,
                  })}
                  last4={listItem.cardLastFour}
                />
              </li>
              <li>
                <Text size='small' color='light'>
                  {t('Date Created')}
                </Text>
                <Text size={showCompactListRow ? 'medium' : 'large'}>
                  {formatDate(listItem.submittedAt, 'MMM D, YYYY')}
                </Text>
              </li>
            </NakedUl>
          </ListRow.Content.TertiaryTitle>
        </ListRow.Content>
      </ListRow>
    );
  };

  return infiniteQueryProps.isLoading ? (
    <SkeletonListLoader />
  ) : (
    <InfinitePaginatedList
      emptyStateConfig={{ type: 'payments', header: t('No data to display') }}
      renderListItem={RenderListItem}
      infiniteQueryProps={infiniteQueryProps}
    />
  );
};

export type PrintFailedListProps = {
  onClose: () => void;
  headerContent?: React.ReactNode;
  filter: SearchFailedPaymentsParams;
  currentPage: number;
};

export const PrintFailedList = ({ onClose, headerContent, filter, currentPage }: PrintFailedListProps) => {
  const { transactions, loading } = useQueryAllFailedInvoices({
    searchParams: filter,
    currentPage,
    numRows: filter.first,
  });

  const columns = useGenerateColumns<SearchFailedPaymentsModel>(generateColumns, true, PaymentsTableInstances.Failed);

  return loading ? (
    <ContentLoader show={loading} />
  ) : !transactions?.length ? null : (
    <PrintDialog show={true} onClose={onClose}>
      <div
        css={css`
          margin: ${theme.spacing(2)};
        `}
      >
        <PrintHeader />
        {headerContent}
        <Table colConfig={columns} data={transactions} tableInstanceId={PaymentsTableInstances.Failed} />
      </div>
    </PrintDialog>
  );
};
